import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Paper,
} from "@mui/material";

const ROADMAP_STATUSES = ["New", "Reviewing", "Approved", "Now", "Next", "Later", "Parked", "Rejected"];
const AREAS = [
  "Sourcing",
  "Scraping",
  "Purchase Requests",
  "Purchase Orders",
  "Listings",
  "Shipping",
  "eBay Intelligence",
  "Profit Tracking",
  "DEV IDE",
  "Automation",
  "Strategy",
];

const emptyForm = {
  title: "",
  area: "Strategy",
  problem_statement: "",
  opportunity_statement: "",
  notes: "",
  roadmap_status: "New",
  revenue_impact: 0,
  time_saving: 0,
  error_reduction: 0,
  strategic_value: 0,
  build_effort: 0,
  confidence: 0,
};

const scoreFields = [
  ["revenue_impact", "Revenue impact"],
  ["time_saving", "Time saving"],
  ["error_reduction", "Error reduction"],
  ["strategic_value", "Strategic value"],
  ["build_effort", "Build effort"],
  ["confidence", "Confidence"],
];

function toNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function calculatePriorityScore(row) {
  return (
    toNumber(row.revenue_impact) +
    toNumber(row.time_saving) +
    toNumber(row.error_reduction) +
    toNumber(row.strategic_value) +
    toNumber(row.confidence) -
    toNumber(row.build_effort)
  );
}

function formatDate(value) {
  if (!value) return "-";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

export default function StrategyPlanner() {
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [query, setQuery] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [gptExportText, setGptExportText] = useState("");
  const [gptImportText, setGptImportText] = useState("");
  const [gptPreview, setGptPreview] = useState(null);
  const [selectedImportRows, setSelectedImportRows] = useState([]);
  const [gptBusy, setGptBusy] = useState(false);

  const loadIdeas = async () => {
    setLoading(true);
    setError("");
    try {
      const params = {};
      if (statusFilter !== "All") params.roadmap_status = statusFilter;
      if (query.trim()) params.q = query.trim();

      const res = await axios.get("/api/strategy-planner/ideas", { params });
      setIdeas(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || "Failed to load strategy ideas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadIdeas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const rankedIdeas = useMemo(() => {
    return [...ideas].sort((a, b) => {
      const scoreDiff = calculatePriorityScore(b) - calculatePriorityScore(a);
      if (scoreDiff !== 0) return scoreDiff;
      return String(b.updated_at || "").localeCompare(String(a.updated_at || ""));
    });
  }, [ideas]);

  const statusCounts = useMemo(() => {
    const counts = {};
    ROADMAP_STATUSES.forEach((status) => {
      counts[status] = 0;
    });
    ideas.forEach((idea) => {
      const status = idea.roadmap_status || "New";
      counts[status] = (counts[status] || 0) + 1;
    });
    return counts;
  }, [ideas]);

  const roadmapGroups = useMemo(() => {
    const groups = {};
    ["Now", "Next", "Later", "Parked"].forEach((status) => {
      groups[status] = rankedIdeas.filter((idea) => idea.roadmap_status === status).slice(0, 5);
    });
    return groups;
  }, [rankedIdeas]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setDialogOpen(true);
    setMessage("");
    setError("");
  };

  const openEdit = (idea) => {
    setEditingId(idea.id);
    setForm({
      ...emptyForm,
      ...idea,
      priority_score: calculatePriorityScore(idea),
    });
    setDialogOpen(true);
    setMessage("");
    setError("");
  };

  const closeDialog = () => {
    if (saving) return;
    setDialogOpen(false);
  };

  const setField = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: scoreFields.some(([key]) => key === field) ? toNumber(value) : value,
    }));
  };

  const saveIdea = async () => {
    setSaving(true);
    setError("");
    setMessage("");

    try {
      if (!form.title.trim()) {
        setError("Title is required.");
        return;
      }

      const payload = { ...form };
      if (editingId) {
        await axios.put(`/api/strategy-planner/ideas/${editingId}`, payload);
        setMessage("Strategy idea updated.");
      } else {
        await axios.post("/api/strategy-planner/ideas", payload);
        setMessage("Strategy idea created.");
      }

      setDialogOpen(false);
      await loadIdeas();
    } catch (err) {
      setError(err?.response?.data?.error || err.message || "Failed to save strategy idea");
    } finally {
      setSaving(false);
    }
  };

  const deleteIdea = async (idea) => {
    const ok = window.confirm(`Delete strategy idea "${idea.title}"?`);
    if (!ok) return;

    setError("");
    setMessage("");

    try {
      await axios.delete(`/api/strategy-planner/ideas/${idea.id}`);
      setMessage("Strategy idea deleted.");
      await loadIdeas();
    } catch (err) {
      setError(err?.response?.data?.error || err.message || "Failed to delete strategy idea");
    }
  };



  const exportForGpt = async () => {
    setGptBusy(true);
    setError("");
    setMessage("");
    try {
      const res = await axios.get("/api/strategy-planner/gpt-export");
      const text = JSON.stringify(res.data?.data || {}, null, 2);
      setGptExportText(text);
      setMessage("GPT export package generated. Copy it into ChatGPT for review.");
      try {
        await navigator.clipboard.writeText(text);
        setMessage("GPT export package generated and copied to clipboard.");
      } catch {
        // Clipboard can fail on some mobile browsers; the text area remains available.
      }
    } catch (err) {
      setError(err?.response?.data?.error || err.message || "Failed to export Strategy Planner GPT package");
    } finally {
      setGptBusy(false);
    }
  };

  const previewGptImport = async () => {
    setGptBusy(true);
    setError("");
    setMessage("");
    setGptPreview(null);
    setSelectedImportRows([]);
    try {
      const res = await axios.post("/api/strategy-planner/gpt-import/preview", {
        review_json: gptImportText,
      });
      const preview = res.data?.data || null;
      setGptPreview(preview);
      const selectableRows = Array.isArray(preview?.rows) ? preview.rows.filter((row) => row.selectable) : [];
      setSelectedImportRows(selectableRows.map((row) => row.row_key));
      setMessage(`GPT import preview ready: ${preview?.summary?.creates || 0} creates, ${preview?.summary?.updates || 0} updates, ${preview?.summary?.skips || 0} skips.`);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || "Failed to preview GPT import JSON");
    } finally {
      setGptBusy(false);
    }
  };

  const toggleImportRow = (rowKey) => {
    setSelectedImportRows((prev) => {
      if (prev.includes(rowKey)) return prev.filter((item) => item !== rowKey);
      return [...prev, rowKey];
    });
  };

  const applyGptImport = async () => {
    if (!gptPreview) {
      setError("Preview the GPT import before applying changes.");
      return;
    }
    if (selectedImportRows.length === 0) {
      setError("Select at least one preview row to apply.");
      return;
    }

    const ok = window.confirm(`Apply ${selectedImportRows.length} selected GPT review change(s)?`);
    if (!ok) return;

    setGptBusy(true);
    setError("");
    setMessage("");
    try {
      const res = await axios.post("/api/strategy-planner/gpt-import/apply", {
        review_json: gptImportText,
        selected_row_keys: selectedImportRows,
        source: "strategy_planner_gpt_review",
      });
      const summary = res.data?.data?.summary || {};
      setMessage(`GPT import applied: ${summary.created || 0} created, ${summary.updated || 0} updated, ${summary.skipped || 0} skipped.`);
      setGptPreview(null);
      setSelectedImportRows([]);
      await loadIdeas();
    } catch (err) {
      setError(err?.response?.data?.error || err.message || "Failed to apply GPT import changes");
    } finally {
      setGptBusy(false);
    }
  };

  const priorityPreview = calculatePriorityScore(form);

  return (
    <Box>
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" gap={2} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800 }}>
            Strategy Planner
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", maxWidth: 820 }}>
            Capture app and business ideas, score impact versus effort, and keep a practical roadmap ranked by priority.
          </Typography>
        </Box>
        <Stack direction="row" gap={1} alignItems="center">
          <Button variant="outlined" onClick={loadIdeas} disabled={loading}>
            Refresh
          </Button>
          <Button variant="contained" onClick={openCreate}>
            Add idea
          </Button>
        </Stack>
      </Stack>

      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
      {message ? <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert> : null}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={3}>
          <Card variant="outlined" sx={{ borderRadius: 3 }}>
            <CardContent>
              <Typography variant="overline">Ranked backlog</Typography>
              <Typography variant="h4" sx={{ fontWeight: 800 }}>
                {rankedIdeas.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Ideas sorted by priority score.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        {["Now", "Next", "Later"].map((status) => (
          <Grid item xs={12} md={3} key={status}>
            <Card variant="outlined" sx={{ borderRadius: 3 }}>
              <CardContent>
                <Typography variant="overline">{status}</Typography>
                <Typography variant="h4" sx={{ fontWeight: 800 }}>
                  {statusCounts[status] || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Roadmap status count.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Card variant="outlined" sx={{ borderRadius: 3, mb: 2 }}>
        <CardContent>
          <Stack direction={{ xs: "column", md: "row" }} gap={2} alignItems={{ xs: "stretch", md: "center" }}>
            <TextField
              label="Search"
              size="small"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") loadIdeas();
              }}
              sx={{ minWidth: 260 }}
            />
            <TextField
              select
              label="Roadmap status"
              size="small"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              sx={{ minWidth: 190 }}
            >
              <MenuItem value="All">All statuses</MenuItem>
              {ROADMAP_STATUSES.map((status) => (
                <MenuItem key={status} value={status}>
                  {status}
                </MenuItem>
              ))}
            </TextField>
            <Button variant="outlined" onClick={loadIdeas} disabled={loading}>
              Apply filters
            </Button>
            {loading ? <CircularProgress size={22} /> : null}
          </Stack>
        </CardContent>
      </Card>



      <Card variant="outlined" sx={{ borderRadius: 3, mb: 2 }}>
        <CardContent>
          <Stack gap={2}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                GPT Strategy Exchange
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Export the current strategy backlog for ChatGPT review, then paste the returned JSON here to preview and apply safe selected changes. This app does not call GPT directly.
              </Typography>
            </Box>

            <Stack direction={{ xs: "column", md: "row" }} gap={1}>
              <Button variant="outlined" onClick={exportForGpt} disabled={gptBusy}>
                Export for GPT
              </Button>
              <Button variant="outlined" onClick={previewGptImport} disabled={gptBusy || !gptImportText.trim()}>
                Preview GPT Review
              </Button>
              <Button variant="contained" onClick={applyGptImport} disabled={gptBusy || !gptPreview || selectedImportRows.length === 0}>
                Apply selected changes
              </Button>
              {gptBusy ? <CircularProgress size={22} /> : null}
            </Stack>

            {gptExportText ? (
              <TextField
                label="GPT export package"
                value={gptExportText}
                onChange={(e) => setGptExportText(e.target.value)}
                fullWidth
                multiline
                minRows={5}
                helperText="Copy this package into ChatGPT. Ask it to return only JSON matching the included import_contract."
              />
            ) : null}

            <TextField
              label="Paste GPT review JSON"
              value={gptImportText}
              onChange={(e) => setGptImportText(e.target.value)}
              fullWidth
              multiline
              minRows={5}
              helperText="Expected top-level shape: { review_summary: string, ideas: [...] }. Preview validates this before any write."
            />

            {gptPreview ? (
              <Box>
                <Stack direction={{ xs: "column", md: "row" }} gap={1} alignItems={{ xs: "stretch", md: "center" }} sx={{ mb: 1 }}>
                  <Chip size="small" label={`Creates: ${gptPreview.summary?.creates || 0}`} />
                  <Chip size="small" label={`Updates: ${gptPreview.summary?.updates || 0}`} />
                  <Chip size="small" label={`Skips: ${gptPreview.summary?.skips || 0}`} />
                  <Typography variant="body2" color="text.secondary">
                    {gptPreview.review_summary || "No review summary supplied."}
                  </Typography>
                </Stack>
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 800 }}>Apply</TableCell>
                        <TableCell sx={{ fontWeight: 800 }}>Action</TableCell>
                        <TableCell sx={{ fontWeight: 800 }}>Idea</TableCell>
                        <TableCell sx={{ fontWeight: 800 }}>Changed fields</TableCell>
                        <TableCell sx={{ fontWeight: 800 }}>Reason</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(gptPreview.rows || []).map((row) => (
                        <TableRow key={row.row_key} hover>
                          <TableCell>
                            <Checkbox
                              size="small"
                              checked={selectedImportRows.includes(row.row_key)}
                              disabled={!row.selectable}
                              onChange={() => toggleImportRow(row.row_key)}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip size="small" label={row.action} color={row.action === "skip" ? "default" : "primary"} />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              {row.title || "Untitled"}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {row.id ? `ID ${row.id}` : "New idea"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Stack gap={0.5}>
                              {(row.changed_fields || []).map((change) => (
                                <Typography variant="caption" key={`${row.row_key}-${change.field}`}>
                                  <strong>{change.field}</strong>: {String(change.before ?? "-")} → {String(change.after ?? "-")}
                                </Typography>
                              ))}
                              {(row.changed_fields || []).length === 0 ? <Typography variant="caption">None</Typography> : null}
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">
                              {row.reason || "-"}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            ) : null}
          </Stack>
        </CardContent>
      </Card>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        {Object.entries(roadmapGroups).map(([status, rows]) => (
          <Grid item xs={12} md={3} key={status}>
            <Card variant="outlined" sx={{ borderRadius: 3, height: "100%" }}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
                  {status}
                </Typography>
                <Stack gap={1}>
                  {rows.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No ideas.
                    </Typography>
                  ) : (
                    rows.map((idea) => (
                      <Box key={idea.id} sx={{ border: "1px solid #e5e7eb", borderRadius: 2, p: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {idea.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Score {calculatePriorityScore(idea)} • {idea.area || "No area"}
                        </Typography>
                      </Box>
                    ))
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 800 }}>Priority</TableCell>
              <TableCell sx={{ fontWeight: 800 }}>Title</TableCell>
              <TableCell sx={{ fontWeight: 800 }}>Area</TableCell>
              <TableCell sx={{ fontWeight: 800 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 800 }}>Impact scores</TableCell>
              <TableCell sx={{ fontWeight: 800 }}>Updated</TableCell>
              <TableCell sx={{ fontWeight: 800 }} align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rankedIdeas.map((idea) => (
              <TableRow key={idea.id} hover>
                <TableCell>
                  <Chip label={calculatePriorityScore(idea)} color={calculatePriorityScore(idea) >= 20 ? "success" : "default"} />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {idea.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {idea.problem_statement || idea.opportunity_statement || idea.notes || "No notes captured yet."}
                  </Typography>
                </TableCell>
                <TableCell>{idea.area || "-"}</TableCell>
                <TableCell>
                  <Chip size="small" label={idea.roadmap_status || "New"} />
                </TableCell>
                <TableCell>
                  <Typography variant="caption">
                    Rev {idea.revenue_impact} • Time {idea.time_saving} • Error {idea.error_reduction} • Strat {idea.strategic_value} • Conf {idea.confidence} • Effort {idea.build_effort}
                  </Typography>
                </TableCell>
                <TableCell>{formatDate(idea.updated_at)}</TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button size="small" variant="outlined" onClick={() => openEdit(idea)}>
                      Edit
                    </Button>
                    <Button size="small" color="error" onClick={() => deleteIdea(idea)}>
                      Delete
                    </Button>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}

            {rankedIdeas.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography variant="body2" color="text.secondary">
                    No strategy ideas found. Add the first idea to begin ranking the roadmap.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="md">
        <DialogTitle sx={{ fontWeight: 800 }}>
          {editingId ? "Edit strategy idea" : "Add strategy idea"}
        </DialogTitle>
        <DialogContent dividers>
          <Stack gap={2} sx={{ mt: 1 }}>
            <TextField
              label="Idea title"
              value={form.title}
              onChange={(e) => setField("title", e.target.value)}
              required
              fullWidth
            />

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  label="Area"
                  value={form.area || ""}
                  onChange={(e) => setField("area", e.target.value)}
                  fullWidth
                >
                  {AREAS.map((area) => (
                    <MenuItem key={area} value={area}>
                      {area}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  label="Roadmap status"
                  value={form.roadmap_status || "New"}
                  onChange={(e) => setField("roadmap_status", e.target.value)}
                  fullWidth
                >
                  {ROADMAP_STATUSES.map((status) => (
                    <MenuItem key={status} value={status}>
                      {status}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>

            <TextField
              label="Problem statement"
              value={form.problem_statement || ""}
              onChange={(e) => setField("problem_statement", e.target.value)}
              fullWidth
              multiline
              minRows={2}
            />

            <TextField
              label="Opportunity statement"
              value={form.opportunity_statement || ""}
              onChange={(e) => setField("opportunity_statement", e.target.value)}
              fullWidth
              multiline
              minRows={2}
            />

            <TextField
              label="Notes"
              value={form.notes || ""}
              onChange={(e) => setField("notes", e.target.value)}
              fullWidth
              multiline
              minRows={3}
            />

            <Divider />

            <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                  Priority scoring
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Score = revenue + time saving + error reduction + strategic value + confidence - build effort.
                </Typography>
              </Box>
              <Chip label={`Priority score: ${priorityPreview}`} color={priorityPreview >= 20 ? "success" : "default"} />
            </Stack>

            <Grid container spacing={2}>
              {scoreFields.map(([field, label]) => (
                <Grid item xs={6} md={4} key={field}>
                  <TextField
                    label={label}
                    type="number"
                    value={form[field] ?? 0}
                    onChange={(e) => setField(field, e.target.value)}
                    fullWidth
                    inputProps={{ min: 0, max: 10, step: 1 }}
                  />
                </Grid>
              ))}
            </Grid>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={saveIdea} variant="contained" disabled={saving}>
            {saving ? "Saving..." : "Save idea"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
